import {
  isObject,
  isWindowed,
  type Obj,
  operationsProblem,
} from "@/pages/pipelineOperations";
import { closestMatches } from "./closestMatches";
import type { Path, PipelineDocError } from "./pipelineLint";
import { checkRoutes, routeBlockIds } from "./pipelineRoutesBlock";

// Lint for the parts of a pipeline that choose operations: the pipeline's
// `operation_types`, its named `routes`, and each stage's `route_id`. The
// rules are the engine's, so what it would refuse at deploy is marked while
// the code is still being written. The engine stays the authority: nothing
// here is stricter than it.
//
// Operation values are free text. Custom values and the empty string are
// legal and are never trimmed or matched against a fixed list; only an empty
// list is a mistake. `null` means "not set", as it does to the engine, for
// `operation_types` and for a stage's `route_id`.

/** A stage position a `route_id` can sit on. */
interface StagePosition {
  stage: unknown;
  label: string;
  path: Path;
}

function checkOperationTypes(doc: Obj, errors: PipelineDocError[]): void {
  const value = doc.operation_types;
  if (value === undefined || value === null) return;
  const problem = operationsProblem(value);
  if (!problem) return;
  const hint = Array.isArray(value) && value.length === 0;
  errors.push({
    path: ["operation_types"],
    message: `"operation_types" ${problem}${hint ? ". Remove it to take every operation" : ""}`,
  });
}

/** The error for one stage's `route_id`, or null when it resolves or the
 *  stage sets none. */
function routeRefError(
  position: StagePosition,
  blockIds: Set<string>,
): PipelineDocError | null {
  const { stage, label, path } = position;
  if (!isObject(stage) || stage.route_id == null) return null;
  const ref = stage.route_id;
  const refPath: Path = [...path, "route_id"];
  if (typeof ref !== "string") {
    return { path: refPath, message: `${label} route_id must be a string` };
  }
  if (isPlaceholder(stage)) {
    return {
      path: refPath,
      message: `${label} has a route_id but no service yet: choose the service and resource first`,
    };
  }
  return blockIds.has(ref)
    ? null
    : unknownRouteError(label, refPath, ref, blockIds);
}

/** A windowed pipeline's middlewares run over window results, which the
 *  engine does not route yet: only its source and sinks may name a route. */
function windowedMiddlewareRouteError(
  position: StagePosition,
): PipelineDocError | null {
  const { label, path } = position;
  const routed = path[0] === "middlewares" && setsRoute(position.stage);
  return routed
    ? {
        path: [...path, "route_id"],
        message: `${label} route_id: a windowed pipeline does not route its middlewares. Route window results with route_id on sinks`,
      }
    : null;
}

/** A stage's route error in a windowed pipeline: a routed middleware first,
 *  since no route it names could be right. */
function windowedRouteRefError(
  position: StagePosition,
  blockIds: Set<string>,
): PipelineDocError | null {
  return (
    windowedMiddlewareRouteError(position) ?? routeRefError(position, blockIds)
  );
}

/** Whether a stage names a route at all. */
function setsRoute(stage: unknown): boolean {
  return isObject(stage) && stage.route_id != null;
}

/** An empty placeholder has no staged row to keep its route on: choosing the
 *  stage comes first, so the switch to Stages never drops the rule. */
function isPlaceholder(stage: Obj): boolean {
  return stage.service_name === "" && stage.resource === "";
}

/** A `route_id` the block does not define. The valid ids are the handful
 *  the block defines: when none is close, offer them all. */
function unknownRouteError(
  label: string,
  path: Path,
  ref: string,
  blockIds: Set<string>,
): PipelineDocError {
  const close = closestMatches(ref, blockIds);
  const replacements = close.length > 0 ? close : [...blockIds].slice(0, 3);
  return {
    path,
    message: `${label} route_id "${ref}" has no matching entry in "routes"`,
    ...(replacements.length > 0
      ? { fix: { kind: "replaceValue", replacements } }
      : {}),
  };
}

/** The pipeline's `operation_types` and a route on the source say the same
 *  thing two ways; the engine takes one. */
function sourceFilterConflict(doc: Obj): PipelineDocError | null {
  const filtersByList =
    doc.operation_types !== undefined && doc.operation_types !== null;
  const source = doc.source;
  const filtersByRoute =
    isObject(source) && typeof source.route_id === "string";
  if (!filtersByList || !filtersByRoute) return null;
  return {
    path: ["source", "route_id"],
    message:
      'source route_id and "operation_types" both filter the source: use one',
  };
}

/** Structural checks for a pipeline's operation rules. `stages` are the
 *  positions a `route_id` can sit on. */
export function checkRouting(
  doc: Obj,
  stages: StagePosition[],
  errors: PipelineDocError[],
): void {
  checkRoutes(doc, errors);
  checkOperationTypes(doc, errors);
  const blockIds = routeBlockIds(doc);
  const refError = isWindowed(doc) ? windowedRouteRefError : routeRefError;
  for (const position of stages) {
    const error = refError(position, blockIds);
    if (error) errors.push(error);
  }
  const conflict = sourceFilterConflict(doc);
  if (conflict) errors.push(conflict);
}
